/**
 * Change History Dialog Component
 * Renders the repo-root CHANGELOG.md as formatted markdown. Opened by clicking
 * the app version in the TopBar. The changelog is imported raw, so Vite HMR keeps
 * it current in dev and the full file (every version section) is inlined into the
 * bundle at build time.
 * @packageDocumentation
 */

import { marked } from 'marked';
import { useMemo } from 'react';
import { History } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from './button';
// Repo-root CHANGELOG.md, six levels up from this file. Imported raw so edits
// hot-reload in dev and the entire file is bundled at build time. Requires
// CHANGELOG.md in the Docker build context — see the `!CHANGELOG.md` rule in
// .dockerignore and the `COPY CHANGELOG.md` in the admin Dockerfile.
import changelogContent from '../../../../../../CHANGELOG.md?raw';
import { Dialog, DialogClose, DialogTitle, DialogFooter, DialogHeader, DialogContent } from './dialog';

/**
 * Props for the ChangeHistoryDialog component.
 */
export interface ChangeHistoryDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Changelog markdown to render. Defaults to the bundled CHANGELOG.md; overridable for tests. */
  changelog?: string;
  /** Called when the open state should change (backdrop, Esc, or close button). */
  onOpenChange: (open: boolean) => void;
}

/** Tailwind element styles for the rendered markdown (the prose plugin is not installed). */
const MARKDOWN_CLASSES = [
  'max-w-none text-sm leading-relaxed text-foreground',
  // Version headings (## Unreleased, ## v2026.7.24) — bold and prominent, spaced from the prior section.
  '[&_h1]:mt-8 [&_h1]:mb-4 [&_h1]:text-xl [&_h1]:font-bold [&_h1:first-child]:mt-0',
  '[&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-lg [&_h2]:font-bold [&_h2:first-child]:mt-0',
  '[&_h3]:mt-5 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold',
  '[&_p]:my-3',
  '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6',
  '[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-6',
  '[&_li]:pl-1',
  '[&_a]:text-primary [&_a]:underline hover:[&_a]:no-underline',
  '[&_strong]:font-semibold [&_strong]:text-foreground',
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]',
  // Section dividers between version blocks.
  '[&_hr]:my-7 [&_hr]:border-t [&_hr]:border-border',
].join(' ');

/**
 * Dialog that displays the application's full changelog history.
 *
 * @param { ChangeHistoryDialogProps } props Component props.
 * @returns { JSX.Element } The rendered change-history dialog.
 */
export const ChangeHistoryDialog = ({ open, onOpenChange, changelog = changelogContent }: ChangeHistoryDialogProps) => {
  const { t } = useTranslation();

  const html = useMemo(() => (changelog.trim() ? (marked.parse(changelog) as string) : ''), [changelog]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="size-5 text-primary" aria-hidden="true" />
            {t('changeHistory.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="-mr-2 max-h-[70vh] overflow-y-auto pr-2">
          {html ? (
            <div
              className={MARKDOWN_CLASSES}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted static markdown authored by us, bundled at build time
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('changeHistory.empty')}</p>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="default" size="sm">
              {t('actions.close')}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
